import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useHistory } from 'react-router-dom';
import { resetPassword } from '../redux/AuthCRUD';
import { PageDataProvider, useLayout } from '../../../../_metronic/layout/core';

const recaptchaRef = React.createRef<ReCAPTCHA>();
const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA || '';

const schema = Yup.object().shape({
  newPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Maximum 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[\^$*.[\]{}()?\-"!@#%&/,><':;|_~`]/, 'Must contain at least one special character'),
  confirmPassword: Yup.string()
    .min(8, 'Maximum 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[\^$*.[\]{}()?\-"!@#%&/,><':;|_~`]/, 'Must contain at least one special character')
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Password confirmation is required')
});

const initialValues = {
  newPassword: '',
  confirmPassword: '',
}

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const history = useHistory();
  const { classes } = useLayout()

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setTimeout(() => {
        resetPassword(email, values.newPassword, values.confirmPassword, token, captchaToken)
          .then(() => {
            history.push('/login');
          })
          .catch(({ response: { data: { message } } }) => {
            recaptchaRef.current?.reset();
            setStatus(message)
          })
          .finally(() => {
            setCaptchaToken(null);
            setSubmitting(false);
          })
      }, 1000)
    },
  });

  const onReCAPTCHAChange = async (captchaToken: string | null) => {
    if (captchaToken) {
      setCaptchaToken(captchaToken);
    }
  };

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (formik.isValid && !formik.isSubmitting) {
        formik.handleSubmit();
      }
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setEmail(params.get('email') || '');
    setToken(params.get('token') || '');
  }, []);

  return (
    <PageDataProvider>
      <div className='page d-flex flex-row flex-column-fluid'>
        <div
          id='kt_aside'
          className={clsx('aside', classes.aside.join(' '))}
          data-kt-drawer='false'
          data-kt-drawer-name='aside'
          data-kt-drawer-activate='{default: true, lg: false}'
          data-kt-drawer-overlay='true'
          data-kt-drawer-width="{default:'200px', '300px': '250px'}"
          data-kt-drawer-direction='start'
          data-kt-drawer-toggle='#kt_aside_mobile_toggle'
          style={{ backgroundColor: 'rgb(243, 229, 80)', width: '30%', justifyContent: 'center' }}
        >
          <div className='container'>
            <div className='mb-20 d-flex aligns-items-center justify-content-center'>
              <Link to={'/'}>
                <img
                  src='/media/logos/C9ondarkbig.png'
                  alt='Delta Panels logo'
                  style={{ width: '247px', height: '65px', objectFit: 'cover' }}
                />
              </Link>
            </div>
            <form
              className='form w-100'
              onSubmit={formik.handleSubmit}
              onKeyDown={handleFormKeyDown}
              noValidate
              id='reset-password-form'>

              {formik.status ? (
                <div className='mb-lg-15 alert alert-warning'>
                  <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
              ) : (
                <></>
              )}

              <div className='text-center mb-10'>
                <h1 className='text-dark mb-3'>Reset Password</h1>
              </div>

              <h6 className="mb-3">Password Guidelines</h6>
              <ul className='fs-7 mb-10'>
                <li>1 Uppercase letter</li>
                <li>1 Lowercase letter</li>
                <li>1 Special character</li>
                <li>1 Number</li>
                <li>Minimum of 8 characters</li>
              </ul>

              <div className='fv-row mb-10'>
                <label className='form-label fs-6 fw-bolder text-dark'>New Password</label>
                <input
                  placeholder='New Password'
                  {...formik.getFieldProps('newPassword')}
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    { 'is-invalid': formik.touched.newPassword && formik.errors.newPassword },
                    {
                      'is-valid': formik.touched.newPassword && !formik.errors.newPassword,
                    }
                  )}
                  type='password'
                  name='newPassword'
                  autoComplete='off'
                />
                {formik.touched.newPassword && formik.errors.newPassword && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.newPassword}</span>
                  </div>
                )}
              </div>

              <div className='fv-row mb-10'>
                <label className='form-label fs-6 fw-bolder text-dark'>Confirm Password</label>
                <input
                  placeholder='Confirm Password'
                  {...formik.getFieldProps('confirmPassword')}
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    { 'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword },
                    {
                      'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
                    }
                  )}
                  type='password'
                  name='confirmPassword'
                  autoComplete='off'
                />
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.confirmPassword}</span>
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-center align-items-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={recaptchaSiteKey}
                  onChange={onReCAPTCHAChange}
                />
              </div>

              <div className="d-flex justify-content-center mt-5">
                <button
                  type='submit'
                  id='kt_reset_password_submit'
                  className='btn btn-lg btn-dark me-2 mb-5 w-50'
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {!formik.isSubmitting && <span className='indicator-label'>SAVE PASSWORD</span>}
                  {formik.isSubmitting && (
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
        <div
          style={{
            marginLeft: '30%',
            width: '100%',
            backgroundImage: `url('/media/misc/delta_banner.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'left center'
          }}
          className='d-flex align-items-center justify-content-center'
        >
        </div>
      </div>
    </PageDataProvider>
  )
}

export { ResetPassword };
